import AsyncButton from "@/components/AsyncButton";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DEFAULT_DATE_FORMAT,
  Module,
  PATH_PROJECT_STORES,
  MIN_PROJECT_DATE,
} from "@/constants";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { CheckIcon } from "lucide-react";
import { forwardRef, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router-dom";
import { ProjectService } from "../service/project";
import { useLocale } from "@/hooks/useLocale";
import { useProjectId } from "@/modules/projects/hooks/useProjectId";
import { DateTime } from "luxon";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";

interface ProjectEndDateUpdateDialogProps {
  open: boolean;
  value?: string;
  onOpenChange: (open: boolean) => void;
}

const ProjectEndDateUpdateDialog = forwardRef<
  HTMLDivElement,
  ProjectEndDateUpdateDialogProps
>(({ open, value, onOpenChange }, ref) => {
  const [successOpen, setSuccessOpen] = useState(false);
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const locale = useLocale();
  const projectId = useProjectId();

  const dateFromValue = useCallback(
    (value?: string) => (value ? new Date(value) : undefined),
    [],
  );

  const [endDate, setEndDate] = useState<Date | undefined>(
    dateFromValue(value),
  );

  useEffect(() => {
    setEndDate(dateFromValue(value));
  }, [dateFromValue, value]);

  const projectMutation = useMutationWithErrorHandling({
    mutationFn: () =>
      ProjectService.updateEndDate(projectId, {
        end_date: format(endDate ?? new Date(), DEFAULT_DATE_FORMAT),
      }),
    onSuccess: () => {
      setSuccessOpen(true);
    },
    errorTitle: t("projects.errors.updateEndDateTitle"),
  });

  const refreshProject = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [Module.Projects, projectId],
    });
  }, [queryClient, projectId]);

  const onOpenChangeInternal = useCallback(
    (open: boolean) => {
      setEndDate(dateFromValue(value));
      onOpenChange(open);
    },
    [dateFromValue, onOpenChange, value],
  );

  const onGoToStoreAssignment = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      onOpenChange(false);
      setSuccessOpen(false);
      navigate(generatePath(PATH_PROJECT_STORES, { projectId }));
    },
    [onOpenChange, navigate, projectId],
  );

  const onSuccessOpenChange = useCallback(
    (open: boolean) => {
      setSuccessOpen(open);
      if (!open) {
        onOpenChange(false);
        refreshProject();
      }
    },
    [onOpenChange, refreshProject],
  );

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChangeInternal}>
        <DialogContent className="sm:max-w-[360px]" ref={ref}>
          <DialogHeader>
            <DialogTitle>
              {t("projects.updateProjectEndDate.title")}
            </DialogTitle>
          </DialogHeader>
          <DialogDescription>
            {t("projects.updateProjectEndDate.description")}
          </DialogDescription>
          <div className="flex justify-center min-h-[355px]">
            <Calendar
              startMonth={MIN_PROJECT_DATE}
              endMonth={DateTime.now()
                .plus({
                  years: 5,
                })
                .toJSDate()}
              captionLayout="dropdown"
              mode="single"
              selected={endDate}
              onSelect={setEndDate}
              autoFocus
              defaultMonth={endDate}
              locale={locale}
              disabled={(date) => date < MIN_PROJECT_DATE}
            />
          </div>
          <DialogFooter className="flex justify-end gap-2">
            <DialogClose asChild>
              <Button variant="secondary">
                {t("projects.updateProjectEndDate.cancel")}
              </Button>
            </DialogClose>
            <AsyncButton
              label={t("projects.updateProjectEndDate.submit")}
              icon={<CheckIcon className="mr-2 h-4 w-4" />}
              onClick={() => projectMutation.mutate(undefined)}
              loading={projectMutation.isPending}
            />
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={open && successOpen} onOpenChange={onSuccessOpenChange}>
        <DialogContent className="sm:max-w-[520px]" ref={ref}>
          <DialogHeader>
            <DialogTitle>
              {t("projects.success.updateEndDateTitle")}
            </DialogTitle>
          </DialogHeader>
          <DialogDescription>
            {t("projects.success.updateEndDateDescription", {
              endDate: format(endDate ?? new Date(), DEFAULT_DATE_FORMAT),
            })}
          </DialogDescription>
          <DialogFooter className="flex justify-end gap-2">
            <Button variant="link" onClick={onGoToStoreAssignment}>
              {t("projects.updateProjectEndDate.goToStoreAssignment")}
            </Button>

            <DialogClose asChild>
              <Button variant="secondary">
                {t("projects.updateProjectEndDate.close")}
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
});

ProjectEndDateUpdateDialog.displayName = "ProjectEndDateUpdateDialog";

export default ProjectEndDateUpdateDialog;
